import { useMutation } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { AgendaAppointmentModalAction } from '../../../data/data/enums';
import { AppointmentModalData } from '../../../data/data/types';
import { consultTypeToSlotType } from '../../../data/data/utils';
import {
  AgendaAppointmentItemDataFragment,
  ConsultType,
  GetAgendaItemsDocument,
  SlotType,
  UpdateAgendaAppointmentItemStatusDocument,
} from '../../../data/types/api/graphql';
import { useFormatters } from '../../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { useNavigateWithHealthProvider } from '../../../data/utils/router.utils';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../elements/button/button';
import { Tag, TagType } from '../../../elements/tag/tag';
import { getAgendaAppointmentAppointmentInput } from '../modals/agenda-appointment-modal/agenda-appointment-modal';

type AgendaAppointmentToggleListItemProps = {
  healthProviderId: string | null | undefined;
  healthProfessionalId: string | null | undefined;
  agendaAppointmentItem: AgendaAppointmentItemDataFragment;
  compact?: boolean;
  showAppointmentModal?: (
    slotType: SlotType,
    value: AppointmentModalData | null
  ) => void;
};

const consultTypeToClass = (consultType: ConsultType) => {
  if (consultType === ConsultType.Telephone) {
    return '--telephone';
  }
  if (consultType === ConsultType.Visit) {
    return '--visit';
  }
  if (consultType === ConsultType.Bulk) {
    return '--bulk';
  }
  return '';
};

export function AgendaAppointmentToggleListItem({
  healthProviderId,
  agendaAppointmentItem,
  compact,
  showAppointmentModal,
}: AgendaAppointmentToggleListItemProps) {
  const { getFullName, getBirthDate } = useFormatters();

  const onSelect = useCallback(() => {
    showAppointmentModal?.(
      consultTypeToSlotType(agendaAppointmentItem.consultType),
      {
        action: AgendaAppointmentModalAction.DEFAULT,
        healthProviderId,
        agendaAppointmentItem: getAgendaAppointmentAppointmentInput(
          agendaAppointmentItem
        ),
      }
    );
  }, [agendaAppointmentItem, healthProviderId, showAppointmentModal]);

  const attributes = useMemo(
    () => ({
      role: 'button',
      tabIndex: 0,
      onClick: () => {
        onSelect();
      },
      onKeyDown: makeKeyDownHandler(() => onSelect()),
    }),
    [onSelect]
  );

  const [
    updateAgendaAppointmentItemStatus,
    { loading: updateAgendaAppointmentItemStatusLoading },
  ] = useMutation(UpdateAgendaAppointmentItemStatusDocument, {
    refetchQueries: [GetAgendaItemsDocument],
  });

  const onHandleAppointmentStatus = useCallback(() => {
    if (agendaAppointmentItem) {
      updateAgendaAppointmentItemStatus({
        variables: {
          id: agendaAppointmentItem.id,
          status: !agendaAppointmentItem?.isCompleted,
        },
      });
    }
  }, [agendaAppointmentItem, updateAgendaAppointmentItemStatus]);

  const navigate = useNavigateWithHealthProvider();

  const onHandleAppointmentChat = useCallback(() => {
    if (agendaAppointmentItem) {
      navigate(`/conversations/${agendaAppointmentItem.conversation?.id}`);
    }
  }, [agendaAppointmentItem, navigate]);

  return (
    <div
      {...attributes}
      className={`appointment-slot-list-item appointment-slot-list-item${consultTypeToClass(
        agendaAppointmentItem.consultType
      )}${agendaAppointmentItem.isCompleted ? ' completed' : ''}${
        compact ? ' compact' : ''
      }`}
    >
      <div className="appointment-slot-list-item__status">
        <Button
          type={ButtonType.TRANSPARENT}
          icon={ButtonIcon.CHECK}
          disabled={updateAgendaAppointmentItemStatusLoading}
          onClick={onHandleAppointmentStatus}
        />
      </div>
      <div className="appointment-slot-list-item__content">
        <p>
          {getFullName(agendaAppointmentItem.patient)}
          <time>{getBirthDate(agendaAppointmentItem.patient)}</time>
        </p>
        {!agendaAppointmentItem.patient?.dutchInsurance && (
          <Tag type={TagType.NOINSURANCE} />
        )}
        {agendaAppointmentItem.patient?.noShowProtocol && (
          <Tag type={TagType.NOSHOWPROTOCOL} />
        )}
      </div>
      <div className="appointment-slot-list-item__action">
        <Button
          type={ButtonType.TRANSPARENT}
          icon={ButtonIcon.COMMENTS}
          onClick={onHandleAppointmentChat}
        />
      </div>
    </div>
  );
}
